import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { HTMLAttributes, VFC } from "react";
import { Markdown } from "components/Markdown";
import { useAnchor } from "hooks";
import { useScreenIsAboveBreakpoint } from "hooks/useScreenIsAboveBreakpoint";
import { DataTestId } from "types/enums/testId";
import { IntroductionContent } from "types/graphql/generated";
import { BusinessSection } from "./components";
import { Card } from "./components/Card";
import { IndividualSection } from "./components/IndividualSection";

interface Props {
  className?: HTMLAttributes<HTMLDivElement>["className"];
  content: IntroductionContent;
}

const Introduction: VFC<Props> = withErrorBoundary(
  ({ content: { title, bgColor, subTitle, accountSetup, compare, businessSection, individualSection } }) => {
    useAnchor("introduction", "id");
    const isAboveMobile = useScreenIsAboveBreakpoint("md");

    return (
      <section data-testid={DataTestId.IntroductionContainer} id="introduction">
        <div className="lg:px-8 lg:py-24" style={{ backgroundColor: bgColor ?? "#FAF6F4" }}>
          <Card className="py-4 md:py-0">
            <div className="flex w-full flex-col gap-14 px-6 py-6 md:px-8 md:py-16 lg:gap-20 lg:px-14">
              <div className="flex flex-col gap-4">
                <h2 className="text-[40px] font-semibold leading-[48px] text-fog-80 md:text-5xl lg:leading-[54px]">
                  {title}
                </h2>
                <Markdown className="max-w-3xl text-2xl text-fog-60">{subTitle}</Markdown>
              </div>
              <div className="flex flex-col gap-6 md:gap-10 lg:flex-row">
                <BusinessSection className="w-full flex-shrink flex-grow lg:w-8/12" content={businessSection} />
                <Markdown className="mb-8 text-base text-fog-60 md:hidden md:text-lg">{compare}</Markdown>
                <IndividualSection
                  className="md:border-t md:border-t-fog-30 md:pt-10 lg:border-l lg:border-t-0 lg:border-l-fog-30 lg:pl-10 lg:pt-0"
                  content={individualSection}
                />
              </div>
            </div>
            {isAboveMobile ? (
              <div className="flex w-full gap-6 border-t border-fog-30 bg-fog-10 px-8 py-12 text-fog-60 lg:gap-10 lg:px-10">
                <Markdown className="border-r border-r-fog-30 pr-6 text-lg lg:pr-10">{compare}</Markdown>
                <Markdown className="text-base md:text-lg">{accountSetup}</Markdown>
              </div>
            ) : (
              <div className=" mx-6 flex self-stretch rounded-lg border-fog-30 bg-fog-10 px-6 py-4 text-fog-60">
                <Markdown className="text-base">{accountSetup}</Markdown>
              </div>
            )}
          </Card>
        </div>
      </section>
    );
  },
  {}
);

gql`
  fragment IntroductionContent on ComponentOrganismsIntroduction {
    bgColor
    title
    subTitle
    accountSetup
    compare
    businessSection {
      ...IntroductionBusinessSectionContent
    }
    individualSection {
      ...IntroductionIndividualSectionContent
    }
  }
`;

export { Introduction };
export type { Props as IntroductionProps };
